import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/auth';
import {
  FeatureFlagContext,
  type FeatureFlagContextValue,
} from '@/feature-flags/FeatureFlagContext';
import {
  featureFlagService,
  type FeatureFlagListResult,
  type FeatureFlagQueryParams,
} from '@/feature-flags/FeatureFlagService';
import type { FeatureFlag } from '@/feature-flags/FeatureEvaluator';
import type {
  FeatureEvaluationContext,
  FeatureFlagEnvironment,
} from '@/feature-flags/FeatureStrategies';
import { publishPlatformEvent } from '@/platform/events';

interface FeatureFlagProviderProps {
  children: ReactNode;
}

const featureFlagModuleIds = new Set(['feature-flags']);

const getAdminModuleId = (pathname: string): string | null => {
  const match = pathname.match(/^\/admin(?:\/embed)?\/([^/]+)/);
  return match?.[1] ?? null;
};

const initialQueryParams: FeatureFlagQueryParams = {
  page: 1,
  pageSize: 8,
  status: 'all',
  productId: 'all',
  environment: 'all',
  type: 'all',
  owner: 'all',
  sortBy: 'lastModified',
  sortDirection: 'desc',
};

export function FeatureFlagProvider({ children }: FeatureFlagProviderProps) {
  const { status, user } = useAuth();
  const { pathname } = useLocation();
  const [queryParams, setQueryParamsState] =
    useState<FeatureFlagQueryParams>(initialQueryParams);
  const queryParamsRef = useRef<FeatureFlagQueryParams>(initialQueryParams);
  const [latestResult, setLatestResult] =
    useState<FeatureFlagListResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [products, setProducts] = useState<Array<{ id: string; name: string }>>(
    [],
  );
  const [environments, setEnvironments] = useState<FeatureFlagEnvironment[]>(
    [],
  );

  useEffect(() => {
    queryParamsRef.current = queryParams;
  }, [queryParams]);

  const queryFlags = useCallback(
    async (params: Partial<FeatureFlagQueryParams> = {}) => {
      setIsLoading(true);

      try {
        const merged = {
          ...queryParamsRef.current,
          ...params,
        };

        const result = await featureFlagService.query(merged);
        queryParamsRef.current = merged;
        setQueryParamsState(merged);
        setLatestResult(result);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    const moduleId = getAdminModuleId(pathname);
    const shouldLoadFeatureFlags =
      moduleId !== null && featureFlagModuleIds.has(moduleId);

    if (status !== 'authenticated' || !shouldLoadFeatureFlags) {
      setLatestResult(null);
      setProducts([]);
      setEnvironments([]);
      setIsLoading(false);
      return;
    }

    void queryFlags();

    void (async () => {
      const [loadedProducts, loadedEnvironments] = await Promise.all([
        featureFlagService.getProducts(),
        featureFlagService.getEnvironments(),
      ]);

      setProducts(loadedProducts);
      setEnvironments(loadedEnvironments);
    })();
  }, [pathname, queryFlags, status]);

  const createFlag = useCallback(
    async (
      payload: Omit<FeatureFlag, 'id' | 'createdDate' | 'lastModified'>,
    ) => {
      const created = await featureFlagService.create(payload);
      await queryFlags();

      publishPlatformEvent({
        type: 'FEATURE_FLAG_UPDATED',
        category: 'featureFlags',
        actor: {
          id: user?.id,
          displayName: user?.displayName ?? 'System Process',
          actorType: user ? 'user' : 'system',
        },
        resource: 'featureFlags',
        resourceId: created.id,
        details: `Feature Flag Created: ${created.key}`,
        severity: 'medium',
        environment: created.environment,
        product: created.productId ?? 'Global',
        ipAddress: '127.0.0.1',
        metadata: {
          action: 'create',
        },
      });

      return created;
    },
    [queryFlags, user],
  );

  const updateFlag = useCallback(
    async (id: string, payload: Partial<FeatureFlag>) => {
      const updated = await featureFlagService.update(id, payload);

      if (!updated) {
        return null;
      }

      await queryFlags();

      publishPlatformEvent({
        type: 'FEATURE_FLAG_UPDATED',
        category: 'featureFlags',
        actor: {
          id: user?.id,
          displayName: user?.displayName ?? 'System Process',
          actorType: user ? 'user' : 'system',
        },
        resource: 'featureFlags',
        resourceId: updated.id,
        details: `Feature Flag Updated: ${updated.key}`,
        severity: 'medium',
        environment: updated.environment,
        product: updated.productId ?? 'Global',
        ipAddress: '127.0.0.1',
        metadata: {
          action: 'update',
        },
      });

      return updated;
    },
    [queryFlags, user],
  );

  const deleteFlag = useCallback(
    async (id: string) => {
      const existing = featureFlagService
        .getSnapshot()
        .find((flag) => flag.id === id);

      await featureFlagService.delete(id);
      await queryFlags();

      publishPlatformEvent({
        type: 'FEATURE_FLAG_UPDATED',
        category: 'featureFlags',
        actor: {
          id: user?.id,
          displayName: user?.displayName ?? 'System Process',
          actorType: user ? 'user' : 'system',
        },
        resource: 'featureFlags',
        resourceId: id,
        details: `Feature Flag Deleted: ${existing?.key ?? id}`,
        severity: 'high',
        environment: existing?.environment ?? 'production',
        product: existing?.productId ?? 'Global',
        ipAddress: '127.0.0.1',
        metadata: {
          action: 'delete',
        },
      });
    },
    [queryFlags, user],
  );

  const updateRollout = useCallback(
    async (id: string, rolloutPercentage: number) => {
      await featureFlagService.updateRollout(id, rolloutPercentage);
      await queryFlags();

      const updated = featureFlagService
        .getSnapshot()
        .find((flag) => flag.id === id);

      publishPlatformEvent({
        type: 'FEATURE_FLAG_UPDATED',
        category: 'featureFlags',
        actor: {
          id: user?.id,
          displayName: user?.displayName ?? 'System Process',
          actorType: user ? 'user' : 'system',
        },
        resource: 'featureFlags',
        resourceId: id,
        details: `Rollout Updated: ${updated?.key ?? id} (${rolloutPercentage}%)`,
        severity: 'medium',
        environment: updated?.environment ?? 'production',
        product: updated?.productId ?? 'Global',
        ipAddress: '127.0.0.1',
        metadata: {
          action: 'update-rollout',
          rolloutPercentage,
        },
      });
    },
    [queryFlags, user],
  );

  const toggleFlag = useCallback(
    async (id: string, enabled: boolean) => {
      const updated = await featureFlagService.update(id, {
        enabled,
        status: enabled ? 'enabled' : 'disabled',
      });

      if (!updated) {
        return;
      }

      await queryFlags();

      publishPlatformEvent({
        type: 'FEATURE_FLAG_UPDATED',
        category: 'featureFlags',
        actor: {
          id: user?.id,
          displayName: user?.displayName ?? 'System Process',
          actorType: user ? 'user' : 'system',
        },
        resource: 'featureFlags',
        resourceId: id,
        details: enabled
          ? `Feature Enabled: ${updated.key}`
          : `Feature Disabled: ${updated.key}`,
        severity: enabled ? 'low' : 'medium',
        environment: updated.environment,
        product: updated.productId ?? 'Global',
        ipAddress: '127.0.0.1',
        metadata: {
          action: enabled ? 'enabled' : 'disabled',
        },
      });
    },
    [queryFlags, user],
  );

  const refresh = useCallback(async () => {
    await featureFlagService.refresh();
    await queryFlags();
  }, [queryFlags]);

  const evaluate = useCallback(
    (key: string, context: FeatureEvaluationContext = {}) => {
      return featureFlagService.evaluate(key, context);
    },
    [],
  );

  const isEnabled = useCallback(
    (key: string, context: FeatureEvaluationContext = {}) => {
      return featureFlagService.isEnabled(key, context);
    },
    [],
  );

  const setQueryParams = useCallback(
    (params: Partial<FeatureFlagQueryParams>) => {
      setQueryParamsState((current) => ({
        ...current,
        ...params,
      }));
    },
    [],
  );

  const value: FeatureFlagContextValue = useMemo(
    () => ({
      flags: latestResult?.items ?? [],
      total: latestResult?.total ?? 0,
      page: latestResult?.page ?? queryParams.page ?? 1,
      pageSize: latestResult?.pageSize ?? queryParams.pageSize ?? 8,
      isLoading,
      queryParams,
      products,
      environments,
      setQueryParams,
      queryFlags,
      createFlag,
      updateFlag,
      deleteFlag,
      updateRollout,
      toggleFlag,
      refresh,
      isEnabled,
      evaluate,
      subscribe: featureFlagService.subscribe.bind(featureFlagService),
      latestResult,
    }),
    [
      createFlag,
      deleteFlag,
      environments,
      evaluate,
      isEnabled,
      isLoading,
      latestResult,
      products,
      queryFlags,
      queryParams,
      refresh,
      setQueryParams,
      toggleFlag,
      updateFlag,
      updateRollout,
    ],
  );

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
}
